// Every reseller, for the staff's resellers page: a card with a search box and a status filter,
// then a table — company (the way to the reseller's page), contact, status, when it signed up —
// whose headers sort it. The search, the filter and the sort are the route's search params
// (guide, rule 16): this draws them and hands up what changed; arrangeResellers applies them.
// The route cuts the page and hands in the caption and the pagination.
import { RESELLER_STATUSES, type PendingReseller, type ResellerStatusFilter } from "@kilat/contracts/reseller";
import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, Eye } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResellerStatusBadge, STATUS_LABELS } from "@/features/reseller/status-badge";
import { m } from "@/lib/paraglide/messages.js";
import { getLocale } from "@/lib/paraglide/runtime.js";

export const RESELLER_SORTS = ["name", "contact", "status", "createdAt"] as const;
export type ResellerSort = (typeof RESELLER_SORTS)[number];
export const SORT_ORDERS = ["asc", "desc"] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

export type ResellerSearch = { q: string; status: ResellerStatusFilter; sort: ResellerSort; order: SortOrder };

/** What the list shows with nothing in the URL: every reseller, newest first. */
export const DEFAULT_RESELLER_SEARCH: ResellerSearch = { q: "", status: "all", sort: "createdAt", order: "desc" };

// A text column reads A to Z first; a date, newest first.
const firstOrder = (sort: ResellerSort): SortOrder => (sort === "createdAt" ? "desc" : "asc");

/**
 * The resellers that match the search (the company or the contact, in any case), in the order
 * asked. Statuses sort in the order an application goes through them; a reseller that sent no
 * information has no contact and goes last either way.
 */
export function arrangeResellers(
  resellers: readonly PendingReseller[],
  { q, sort, order }: Pick<ResellerSearch, "q" | "sort" | "order">,
): PendingReseller[] {
  const needle = q.trim().toLocaleLowerCase();
  const matching = needle
    ? resellers.filter((r) =>
        [r.name, r.information?.contactName ?? ""].some((text) => text.toLocaleLowerCase().includes(needle)),
      )
    : resellers;
  const text = new Intl.Collator(getLocale(), { sensitivity: "base" });
  const direction = order === "asc" ? 1 : -1;
  return matching.toSorted((a, b) => {
    switch (sort) {
      case "name":
        return direction * text.compare(a.name, b.name);
      case "contact": {
        const [x, y] = [a.information?.contactName, b.information?.contactName];
        if (x === undefined || y === undefined) return Number(x === undefined) - Number(y === undefined);
        return direction * text.compare(x, y);
      }
      case "status":
        return direction * (RESELLER_STATUSES.indexOf(a.status) - RESELLER_STATUSES.indexOf(b.status));
      case "createdAt":
        return direction * (Date.parse(a.createdAt) - Date.parse(b.createdAt));
    }
  });
}

type SearchProps = { search: ResellerSearch; onSearchChange: (next: Partial<ResellerSearch>) => void };

/** A column header that sorts by its column; on the column already sorted by, the other way. */
function SortableHead({
  column,
  label,
  search,
  onSearchChange,
}: SearchProps & { column: ResellerSort; label: string }) {
  const active = search.sort === column;
  const Icon = !active ? ArrowUpDown : search.order === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead aria-sort={!active ? "none" : search.order === "asc" ? "ascending" : "descending"}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          onSearchChange(
            active
              ? { sort: column, order: search.order === "asc" ? "desc" : "asc" }
              : { sort: column, order: firstOrder(column) },
          )
        }
      >
        {label}
        <Icon aria-hidden />
      </Button>
    </TableHead>
  );
}

const STATUS_ITEMS = [
  { value: "all", label: m.staff_resellers_status_all },
  ...RESELLER_STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] })),
] satisfies { value: ResellerStatusFilter; label: () => string }[];

function Toolbar({ search, onSearchChange }: SearchProps) {
  const items = STATUS_ITEMS.map(({ value, label }) => ({ value, label: label() }));
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {/* Uncontrolled: the URL is the state, and each keystroke is handed up to it. */}
      <Input
        type="search"
        className="sm:max-w-xs"
        defaultValue={search.q}
        placeholder={m.staff_resellers_search()}
        aria-label={m.staff_resellers_search()}
        onChange={(event) => onSearchChange({ q: event.target.value })}
      />
      <Select
        items={items}
        value={search.status}
        onValueChange={(status: ResellerStatusFilter | null) => status && onSearchChange({ status })}
      >
        <SelectTrigger aria-label={m.staff_resellers_status_filter()}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ResellerTable({
  resellers,
  search,
  onSearchChange,
  caption,
  pagination,
}: SearchProps & {
  resellers: readonly PendingReseller[];
  caption?: string;
  pagination?: ReactNode;
}) {
  const signedUp = new Intl.DateTimeFormat(getLocale(), { dateStyle: "medium" });
  const sortable = { search, onSearchChange };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.staff_resellers_list_title()}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Toolbar {...sortable} />
        {resellers.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m.staff_resellers_no_match_title()}</EmptyTitle>
              <EmptyDescription>{m.staff_resellers_no_match_body()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table>
            {caption && <TableCaption className="text-left">{caption}</TableCaption>}
            <TableHeader>
              <TableRow>
                <SortableHead column="name" label={m.staff_resellers_column_company()} {...sortable} />
                <SortableHead column="contact" label={m.staff_resellers_column_contact()} {...sortable} />
                <SortableHead column="status" label={m.staff_column_status()} {...sortable} />
                <SortableHead column="createdAt" label={m.staff_resellers_column_registered()} {...sortable} />
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {resellers.map((reseller) => (
                <TableRow key={reseller.id}>
                  <TableCell>
                    <Link to="/staff/resellers/$resellerId" params={{ resellerId: reseller.id }}>
                      {reseller.name}
                    </Link>
                  </TableCell>
                  <TableCell>{reseller.information?.contactName ?? "-"}</TableCell>
                  <TableCell>
                    <ResellerStatusBadge status={reseller.status} />
                  </TableCell>
                  <TableCell>{signedUp.format(new Date(reseller.createdAt))}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      render={<Link to="/staff/resellers/$resellerId" params={{ resellerId: reseller.id }} />}
                    >
                      <Eye />
                      {m.staff_resellers_details()}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {pagination}
      </CardContent>
    </Card>
  );
}
